import { useState } from 'react';
import { HttpError } from '@/lib/api';
import { useUpdateIntervention } from '@/hooks/useInterventionMutations';
import { useMechanics } from '@/hooks/useInterventions';
import { useToast } from '@/hooks/useToast';
import type { InterventionDetail } from '@/hooks/useSocket';
import type { TypeAgenda } from '@/lib/types';

// Sépare un ISO (heure-mur = UTC) en {date:'YYYY-MM-DD', time:'HH:MM'}.
function splitIsoUtc(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`,
    time: `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`,
  };
}

export default function EditInterventionForm({
  intervention,
  onDone,
  onCancel,
}: {
  intervention: InterventionDetail;
  onDone: () => void;
  onCancel: () => void;
}) {
  const toast = useToast();
  const update = useUpdateIntervention();
  const mechanicsQuery = useMechanics();

  const init = splitIsoUtc(intervention.creneauDebut);
  const [mecanicienId, setMecanicienId] = useState(intervention.mecanicien.id);
  const [typeAgenda, setTypeAgenda] = useState<TypeAgenda>(intervention.typeAgenda as TypeAgenda);
  const [nature, setNature] = useState(intervention.nature);
  const [description, setDescription] = useState(intervention.description ?? '');
  const [duree, setDuree] = useState(intervention.dureeEstimeeMinutes);
  const [date, setDate] = useState(init.date);
  const [time, setTime] = useState(init.time);
  const [urgent, setUrgent] = useState(intervention.urgent);
  const [notesInternes, setNotesInternes] = useState(intervention.notesInternes ?? '');

  const mecanos = (mechanicsQuery.data ?? [])
    .filter((m) => m.actif)
    .sort((a, b) => a.ordreAffichage - b.ordreAffichage);

  function submit() {
    const creneauDebut = `${date}T${time}:00.000Z`; // heure-mur = UTC
    update.mutate(
      {
        id: intervention.id,
        mecanicienId,
        typeAgenda,
        nature,
        description: description || undefined,
        dureeEstimeeMinutes: duree,
        dateRdv: date,
        creneauDebut,
        urgent,
        notesInternes: notesInternes || undefined,
      },
      {
        onSuccess: () => {
          toast.success('RDV mis à jour');
          onDone();
        },
        onError: (err) => {
          toast.error(err instanceof HttpError ? err.payload.message : 'Erreur de mise à jour');
        },
      },
    );
  }

  return (
    <div data-testid="edit-rdv-form" className="flex-1 overflow-y-auto p-5 space-y-4">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-text-muted">Modifier le RDV</h3>

      <label className="block space-y-1">
        <span className="input-label">Mécanicien</span>
        <select className="input" value={mecanicienId} onChange={(e) => setMecanicienId(e.target.value)}>
          {mecanos.map((m) => (
            <option key={m.id} value={m.id}>{m.prenom} {m.nom}</option>
          ))}
        </select>
      </label>

      <label className="block space-y-1">
        <span className="input-label">Type</span>
        <select className="input" value={typeAgenda} onChange={(e) => setTypeAgenda(e.target.value as TypeAgenda)}>
          <option value="rapide">Atelier rapide</option>
          <option value="mecanique">Atelier mécanique</option>
          <option value="lavage">Lavage</option>
        </select>
      </label>

      <label className="block space-y-1">
        <span className="input-label">Nature</span>
        <input className="input" value={nature} onChange={(e) => setNature(e.target.value)} />
      </label>

      <label className="block space-y-1">
        <span className="input-label">Description</span>
        <textarea className="input py-2 h-auto" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>

      <div className="grid grid-cols-2 gap-3">
        <label className="block space-y-1">
          <span className="input-label">Date RDV</span>
          <input type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="block space-y-1">
          <span className="input-label">Heure début</span>
          <input type="time" step={900} className="input" value={time} onChange={(e) => setTime(e.target.value)} />
        </label>
      </div>

      <label className="block space-y-1">
        <span className="input-label">Durée (min)</span>
        <input type="number" min={15} step={15} className="input w-32" value={duree} onChange={(e) => setDuree(Number(e.target.value))} />
      </label>

      <label className="block space-y-1">
        <span className="input-label">Notes internes</span>
        <textarea className="input py-2 h-auto" rows={2} value={notesInternes} onChange={(e) => setNotesInternes(e.target.value)} />
      </label>

      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" className="w-4 h-4 accent-ktm-orange" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} />
        <span className="input-label !mb-0">Urgent</span>
      </label>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="btn btn-secondary">Annuler</button>
        <button type="button" onClick={submit} disabled={update.isPending || nature.trim().length < 2} className="btn btn-primary">
          {update.isPending ? '…' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
}
